import React from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { Experience } from '@atlassian/experience-tracker/component/Experience';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker/component/ExperienceTrackerContext';

import {
	type ExperienceNames,
	useExperienceTracker,
	useSetUpExperience,
} from '../experience-tracker';
import { Iframe } from '../iframe';
import {
	type EMBEDDED_CONFLUENCE_MODE,
	type EMBEDDED_CONFLUENCE_CONTENT_TYPE,
	Footer,
	PageLoadValidation,
	useFooter,
	useUrl,
	type TemplateType,
	useContentSize,
	useScrollToHashFromEmbed,
} from '../page-common';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

import { type EditPageProps } from '../edit-page/EditPage';
import { type ViewPageProps } from '../view-page/ViewPage';
import type { AnyAllowedFeatures } from '../features';
import { PageNotFoundEmptyState } from '../empty-state';
import messages from './messages';
import { useIntl } from 'react-intl';
import { IframeLoadingScreen } from '../page-common/IframeLoadingScreen';

export type ContentProps = (ViewPageProps | EditPageProps) & {
	allowedFeatures?: AnyAllowedFeatures;
	contentConfiguration: {
		experiences: {
			ccExperience: ExperienceNames;
			experience: ExperienceNames;
		};
		getWrapperElement?: (children: React.ReactNode) => React.ReactElement;
		hasFooter?: boolean;
		queryParams?: { [key: string]: string };
	};
	contentType: EMBEDDED_CONFLUENCE_CONTENT_TYPE;
	draftShareId?: string;
	enableSpaTransitions?: boolean;
	mode?: EMBEDDED_CONFLUENCE_MODE;
	previewPanels?: string;
	shouldShowFooter?: boolean;
	template?: TemplateType;
};

const IframeWrapper = ({
	children,
	wrapper,
}: {
	children: React.ReactNode;
	wrapper?: ContentProps['contentConfiguration']['getWrapperElement'];
}): React.ReactElement => <>{wrapper?.(children) ?? children}</>;

// helper to merge iframe refs
function mergeIframeRefs(...refs: Array<React.Ref<HTMLIFrameElement> | undefined>) {
	return (value: HTMLIFrameElement | null) => {
		refs.forEach((ref) => {
			if (!ref) {
				return;
			}
			if (typeof ref === 'function') {
				ref(value);
			} else {
				(ref as React.MutableRefObject<HTMLIFrameElement | null>).current = value;
			}
		});
	};
}

export const Content = (props: ContentProps): React.JSX.Element => {
	const {
		contentId,
		contentConfiguration,
		navigationPolicy: _navigationPolicy,
		iframeRef: externalIframeRef,
		onLoad,
		embeddedConfluenceSource,
		queryParams: _queryParams,
		spaceKey,
		...rest
	} = props;
	// Broadcast `spaceKey` so the iframe header prefers the parent prop over
	// the URL path, which can carry a stale space alias after a rename (JPNS-115645).
	const passThroughProps = FeatureGates.getExperimentValue<boolean>(
		'embedded_confluence_send_space_key_to_iframe',
		'isEnabled',
		false,
	)
		? { ...rest, spaceKey }
		: rest;

	const { formatMessage } = useIntl();

	const { uniqueKey } = useUniqueKey();

	const experienceTracker = useExperienceTracker();
	const { experience, ccExperience } = contentConfiguration.experiences;

	useSetUpExperience({
		experienceTracker,
		contentId,
		spaceKey,
		experience,
		ccExperience,
	});
	const { embeddedConfluenceUrl, standaloneConfluenceUrl } = useUrl(props);
	/**
	 * The difference between hasFooter and hasFooterLogo can be found here
	 * https://bitbucket.org/atlassian/atlassian-frontend/pull-requests/40212/cbt-2587-docs-changeset-ux-add-embedded#comment-428798112
	 *
	 * EDIT 7/17: hasFooterLogo property has been renamed internally for clarity as is now aliased as shouldShowFooter when used internally. Externally, the API remains unchanged.
	 */
	const shouldShowFooter = (props as ViewPageProps).hasFooterLogo ?? true;
	const { showFooter, footerUrl } = useFooter({
		experienceTracker,
		hasFooter: contentConfiguration.hasFooter,
		shouldShowFooter,
		parentProduct: props.parentProduct,
		standaloneConfluenceUrl,
	});

	const { height } = useContentSize(uniqueKey);

	useScrollToHashFromEmbed(
		passThroughProps.isHeightSetFromContent,
		uniqueKey,
		passThroughProps.scrollToHashHandler,
	);

	return (
		<ExperienceTrackerContext.Provider value={experienceTracker}>
			<Experience name={experience}>
				<PageLoadValidation {...props}>
					{({
						iframeRef,
						iframeLoaded,
						isLoading,
						handleIframeOnLoad,
						handleContentLoadStarted,
						navigationPolicy,
						handleIframeUnload,
						redirectedToLogin,
					}) => (
						<>
							<IframeWrapper wrapper={contentConfiguration.getWrapperElement}>
								{embeddedConfluenceUrl ? (
									<IframeLoadingScreen iframeLoaded={iframeLoaded} isLoading={isLoading}>
										<Iframe
											ref={mergeIframeRefs(iframeRef, externalIframeRef)}
											// https://hello.atlassian.net/wiki/spaces/~142157567/pages/2224394268/Spike+on+CBT-1814
											src={redirectedToLogin ? '' : embeddedConfluenceUrl}
											{...passThroughProps}
											navigationPolicy={navigationPolicy}
											style={{
												flex: passThroughProps.isHeightSetFromContent
													? `1 0 ${height}px`
													: '0 1 100%',
											}}
											uniqueKey={uniqueKey}
											onLoad={(e) => {
												handleIframeOnLoad();
												onLoad?.(e);
											}}
											onUnload={handleIframeUnload}
											onContentLoadStarted={handleContentLoadStarted}
										/>
									</IframeLoadingScreen>
								) : (
									<PageNotFoundEmptyState embeddedConfluenceSource={embeddedConfluenceSource} />
								)}
							</IframeWrapper>
							{showFooter && (
								<Footer
									url={footerUrl}
									linkTooltip={formatMessage(messages.viewInConfluence)}
									label={formatMessage(messages.poweredBy)}
								/>
							)}
						</>
					)}
				</PageLoadValidation>
			</Experience>
		</ExperienceTrackerContext.Provider>
	);
};
